/**
 * Contact form image attachment (Shopify contact form does not accept multipart files).
 * - Small images: pre-read on file select, native form submit (Shopify challenge / web pixels safe)
 * - Large images: Cloudinary then requestSubmit (never form.submit())
 */
(function () {
  const MAX_INLINE_DEFAULT = 100 * 1024;

  function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function isImageFile(file) {
    if (!file) return false;
    if (file.type && file.type.indexOf('image/') === 0) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(name);
  }

  function normalizeCloudName(raw) {
    let name = (raw || '').trim();
    if (!name) return '';
    name = name.replace(/^https?:\/\//i, '');
    name = name.replace(/^res\.cloudinary\.com\//i, '');
    name = name.replace(/^api\.cloudinary\.com\/v1_1\//i, '');
    name = name.split('/')[0].split('?')[0];
    return name;
  }

  function readAsDataURL(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onload = function () {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function uploadToCloudinary(file, cloudName, uploadPreset) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', uploadPreset);
    return fetch('https://api.cloudinary.com/v1_1/' + encodeURIComponent(cloudName) + '/image/upload', {
      method: 'POST',
      body: fd,
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          const msg = (data && data.error && data.error.message) || 'Cloudinary upload failed';
          throw new Error(msg);
        }
        return data;
      });
    });
  }

  function setError(root, message) {
    const errorEl = root.querySelector('[data-contact-upload-error]');
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  function updateFileUI(root, file) {
    const labelText = root.querySelector('[data-contact-upload-label-text]');
    const filenameEl = root.querySelector('[data-contact-upload-filename]');
    const clearBtn = root.querySelector('[data-contact-upload-clear]');

    if (!file) {
      if (labelText) labelText.hidden = false;
      if (filenameEl) {
        filenameEl.textContent = '';
        filenameEl.hidden = true;
      }
      if (clearBtn) clearBtn.hidden = true;
      return;
    }

    if (labelText) labelText.hidden = true;
    if (filenameEl) {
      filenameEl.textContent = file.name + ' (' + formatSize(file.size) + ')';
      filenameEl.hidden = false;
    }
    if (clearBtn) clearBtn.hidden = false;
  }

  function applyAttachment(hiddenField, bodyField, attachmentSummary, bodyAppend) {
    hiddenField.value = attachmentSummary;
    if (bodyAppend) {
      const trimmed = bodyField.value.replace(/\s+$/, '');
      bodyField.value = trimmed + bodyAppend;
    }
  }

  function resubmitForm(form, submitBtn) {
    form.dataset.contactAttachmentReady = '1';
    if (typeof form.requestSubmit === 'function' && submitBtn) {
      form.requestSubmit(submitBtn);
    } else if (submitBtn) {
      submitBtn.click();
    } else {
      form.submit();
    }
  }

  function init(root) {
    if (!root || root.dataset.contactUploadInit === '1') return;
    root.dataset.contactUploadInit = '1';

    const form = document.getElementById('ContactForm');
    const fileInput = root.querySelector('[data-contact-upload-input]');
    const hiddenField = root.querySelector('[data-contact-upload-hidden]');
    const bodyField = document.getElementById('ContactForm-body');

    if (!form || !fileInput || !hiddenField || !bodyField) return;

    const maxFileBytes =
      parseInt(root.dataset.maxFileMb || '5', 10) * 1024 * 1024 || 5 * 1024 * 1024;
    const maxInlineBytes = parseInt(root.dataset.maxInlineKb || '100', 10) * 1024 || MAX_INLINE_DEFAULT;
    const cloudName = normalizeCloudName(root.dataset.cloudinaryCloudName);
    const uploadPreset = (root.dataset.cloudinaryUploadPreset || '').trim();
    const strings = window.elinContactUploadStrings || {};
    const hasCloudinary = Boolean(cloudName && uploadPreset);

    let pendingAttachment = null;

    function clearPending() {
      pendingAttachment = null;
      root.dataset.attachmentStatus = '';
    }

    function preloadSmallImage(file) {
      root.dataset.attachmentStatus = 'loading';
      return readAsDataURL(file)
        .then(function (dataUrl) {
          pendingAttachment = {
            summary: file.name + ' (' + formatSize(file.size) + ')',
            bodyAppend: '\n\n--- Attached image ---\n' + dataUrl,
          };
          root.dataset.attachmentStatus = 'ready';
        })
        .catch(function () {
          clearPending();
          root.dataset.attachmentStatus = 'error';
          setError(root, strings.uploadFailed || 'Could not read your image. Please try again.');
        });
    }

    fileInput.addEventListener('change', function () {
      setError(root, '');
      clearPending();
      const file = fileInput.files && fileInput.files[0];
      if (!file) {
        updateFileUI(root, null);
        return;
      }
      if (!isImageFile(file)) {
        fileInput.value = '';
        updateFileUI(root, null);
        setError(root, strings.invalidType || 'Please choose an image file (JPG, PNG, GIF, or WebP).');
        return;
      }
      if (file.size > maxFileBytes) {
        fileInput.value = '';
        updateFileUI(root, null);
        setError(
          root,
          (strings.tooLarge || 'Image must be smaller than {size} MB.').replace(
            '{size}',
            String(Math.round(maxFileBytes / (1024 * 1024)))
          )
        );
        return;
      }
      updateFileUI(root, file);
      if (file.size <= maxInlineBytes) {
        preloadSmallImage(file);
      } else {
        root.dataset.attachmentStatus = 'large';
      }
    });

    const clearBtn = root.querySelector('[data-contact-upload-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        fileInput.value = '';
        hiddenField.value = '';
        updateFileUI(root, null);
        clearPending();
        setError(root, '');
      });
    }

    form.addEventListener('submit', function (e) {
      if (form.dataset.contactAttachmentReady === '1') {
        delete form.dataset.contactAttachmentReady;
        return;
      }

      const file = fileInput.files && fileInput.files[0];
      if (!file) return;

      const submitBtn = form.querySelector('[type="submit"]');

      /* Small image: fields already filled — let Shopify handle native submit + challenge */
      if (file.size <= maxInlineBytes) {
        if (root.dataset.attachmentStatus === 'loading') {
          e.preventDefault();
          setError(root, strings.processing || 'Please wait — your image is still being prepared.');
          return;
        }
        if (pendingAttachment) {
          applyAttachment(
            hiddenField,
            bodyField,
            pendingAttachment.summary,
            pendingAttachment.bodyAppend
          );
        }
        return;
      }

      /* Large image: async upload, then requestSubmit (not form.submit()) */
      e.preventDefault();
      setError(root, '');

      if (submitBtn) {
        submitBtn.setAttribute('disabled', 'disabled');
        submitBtn.setAttribute('aria-busy', 'true');
      }

      const fail = function (message) {
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.removeAttribute('aria-busy');
        }
        setError(root, message || strings.uploadFailed || 'Could not attach your image. Please try again.');
      };

      const done = function (attachmentSummary, bodyAppend) {
        applyAttachment(hiddenField, bodyField, attachmentSummary, bodyAppend);
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.removeAttribute('aria-busy');
        }
        resubmitForm(form, submitBtn);
      };

      if (hasCloudinary) {
        uploadToCloudinary(file, cloudName, uploadPreset)
          .then(function (data) {
            const url = data.secure_url || data.url;
            if (!url) throw new Error('No image URL returned');
            done(
              file.name + ' (' + formatSize(file.size) + ')',
              '\n\n--- Attached image (download link) ---\n' + url
            );
          })
          .catch(function (err) {
            const detail = err && err.message ? err.message : '';
            fail(
              (strings.cloudinaryFailed || 'Image upload failed.') +
                (detail ? ' ' + detail : ' Check Cloudinary cloud name and unsigned upload preset.')
            );
          });
        return;
      }

      fail(
        strings.tooLargeForEmail ||
          'This image is too large to send without Cloudinary. Use a photo under ' +
            Math.round(maxInlineBytes / 1024) +
            ' KB, or configure Cloudinary in theme settings.'
      );
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-contact-upload-root]').forEach(init);
  });
})();
